export const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jfif", "image/webp"]
export const MAX_IMAGE_DIMENSION = 1920
export const TARGET_IMAGE_BYTES = 2 * 1024 * 1024
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024

export class ImageValidationError extends Error {}

export function isSupportedImage(file) {
  return file instanceof Blob && (SUPPORTED_IMAGE_TYPES.includes(file.type) || /\.jfif$/i.test(file.name || ""))
}

export async function optimizeImage(file, {
  maxDimension = MAX_IMAGE_DIMENSION,
  targetBytes = TARGET_IMAGE_BYTES,
  preserveType = false
} = {}) {
  if (!isSupportedImage(file)) {
    throw new ImageValidationError("Unsupported image format. Please use PNG, JPG, or WebP.")
  }

  let image
  try {
    image = await decodeImage(file)
  } catch (_) {
    throw new ImageValidationError("This image is broken or cannot be read.")
  }

  const sourceWidth = image.width
  const sourceHeight = image.height
  if (!sourceWidth || !sourceHeight) {
    closeImage(image)
    throw new ImageValidationError("This image has invalid dimensions.")
  }

  const isJfif = file.type === "image/jfif" || /\.jfif$/i.test(file.name || "")
  const dimensionScale = Math.min(1, maxDimension / sourceWidth, maxDimension / sourceHeight)
  let width = Math.max(1, Math.round(sourceWidth * dimensionScale))
  let height = Math.max(1, Math.round(sourceHeight * dimensionScale))

  if (dimensionScale === 1 && file.size <= targetBytes && !isJfif) {
    closeImage(image)
    return file
  }

  // WebP keeps transparency while generally producing much smaller files than
  // PNG. JPEG inputs remain JPEG to avoid an unnecessary format change.
  const outputType = preserveType && ["image/png", "image/jpeg"].includes(file.type)
    ? file.type
    : (file.type === "image/jpeg" || isJfif ? "image/jpeg" : "image/webp")
  let quality = 0.9
  let blob

  for (let attempt = 0; attempt < 8; attempt++) {
    blob = await renderImage(image, width, height, outputType, quality)
    if (blob.size <= targetBytes) break

    if (quality > 0.62) {
      quality -= 0.08
    } else {
      const sizeScale = Math.max(0.7, Math.sqrt(targetBytes / blob.size) * 0.95)
      width = Math.max(1, Math.round(width * sizeScale))
      height = Math.max(1, Math.round(height * sizeScale))
    }
  }

  closeImage(image)
  if (!blob || blob.size > MAX_IMAGE_BYTES) {
    throw new ImageValidationError("This image is too large to upload.")
  }

  const extension = { "image/jpeg": "jpg", "image/png": "png" }[outputType] || "webp"
  const baseName = file.name?.replace(/\.[^.]+$/, "") || "image"
  return new File([blob], `${baseName}.${extension}`, {
    type: outputType,
    lastModified: file.lastModified || Date.now()
  })
}

async function decodeImage(file) {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" })
    } catch (_) {
      return await createImageBitmap(file)
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

async function renderImage(image, width, height, type, quality) {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) throw new ImageValidationError("Image optimization is not supported by this browser.")
  context.drawImage(image, 0, 0, width, height)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new ImageValidationError("Image optimization failed.")),
      type,
      quality
    )
  })
}

function closeImage(image) {
  if (typeof image.close === "function") image.close()
}
